<template>
	<b-container class='report-page'>
            <b-row>
                <b-col sm="3">
                    <b-form-group>
                        <label>{{ $t('stop_punctuality_report.stop') }}</label>
                        <stopSelect v-model="selectedStop" id="stop"/>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('report.start_date') }}</label>
                        <b-form-datepicker 
                            :placeholder="this.$t('datepicker.labelNoDateSelected')"
                            v-model="startDate" 
                            :locale="this.$t('datepicker.locale')" 
                            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" >
                        </b-form-datepicker>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('report.end_date') }}</label>
                        <b-form-datepicker 
                            :placeholder="this.$t('datepicker.labelNoDateSelected')"
                            v-model="endDate" 
                            :locale="this.$t('datepicker.locale')" 
                            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" >
                        </b-form-datepicker>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('stop_punctuality_report.min_early') }}</label>
                        <vSelect :options="timeOptions" v-model="minEarly" :reduce="option => option.minutes" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group>
                        <label>{{ $t('stop_punctuality_report.min_late') }}</label>
                        <vSelect :options="timeOptions" v-model="minLate" :reduce="option => option.minutes" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row class='btn-row'>
                <b-col>
                    <b-button class="btn-success btn-report" @click="generateReport()" :disabled="buttonDisabled">
                        <b-icon icon="arrow-clockwise" scale="1.2"></b-icon>
                        {{ $t('report.generate') }}
                    </b-button>
                    <b-button class="btn-success btn-report" @click="printReport()" :disabled="buttonDisabled || !generated">
                        <b-icon icon="printer" scale="1.2"></b-icon>
                        {{ $t('report.print') }}
                    </b-button>
                    <b-button class="btn-success btn-report" @click="generateXLS()" :disabled="buttonDisabled || !generated">
                        <b-icon icon="download" scale="1.2"></b-icon>
                        {{ $t('report.download') }}
                    </b-button>
                </b-col>  
            </b-row>
			<b-row>
				<b-col>
                    <b-table
						striped
						hover
						:items="items"
						:fields="fields"
						:per-page="perPage"
						:current-page="currentPage"
                        :busy="loading">

                    <template #table-busy>
                        <Loading />
                    </template>
					
                    <template v-slot:cell(deviation)="row">
                        {{ getDeviation(row.item.departureTime, row.item.realTime) }}
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button size="sm" @click="getRowData(row)" :disabled="detailsLoading" >{{ row.detailsShowing ? '-' : '+' }}</b-button>
                        
                    </template>

                    <template v-slot:row-details="row">
                        <b-card>
                            <!-- <Loading v-if="detailsLoading" /> -->
                            <b-container>
                                <b-row>
                                    <b-col>{{ $t('stop_punctuality_report.line') }}</b-col>
                                    <b-col>{{ $t('stop_punctuality_report.scheduled_time') }}</b-col>
                                    <b-col>{{ $t('stop_punctuality_report.real_time') }}</b-col>
                                    <b-col>{{ $t('stop_punctuality_report.deviation') }}</b-col>
                                </b-row>
                                <b-row v-for="(passing, index) in row.item.passings" :key="index" style="border-top: 1px solid; padding-top: 3px">
                                    <b-col>{{ passing.line }}</b-col>
                                    <b-col>{{ formatTime(passing.passingTime, 'DD.MM.YYYY HH:mm') }}</b-col>
                                    <b-col>{{ formatTime(passing.realTime, 'HH:mm:ss') }}</b-col>
                                    <b-col>{{ getDeviation(passing.passingTime, passing.realTime) }}</b-col>
                                </b-row>
                            </b-container>
                        </b-card>
                    </template>
					</b-table>

					<TablePagination
						:total-rows="totalRows"
						:per-page="perPage"
						@pageChange="onPageChange"
					>
					</TablePagination>
				</b-col>
			</b-row>
	</b-container>
</template>

<script>
import TablePagination from '@/components/Misc/TablePagination';
import stopPunctualityReport from '@/store/realtime/stopPunctualityReport';
import vSelect from 'vue-select'
import moment from 'moment';
import stopSelect from '@/components/Timetable/StopSelect'
import utils from '@/plugins/TravellerUtils.js'

export default {
	components: { TablePagination, stopSelect, vSelect },
	data: function() {
		return {
			perPage: 30,
			currentPage: 1,
            selectedStop: null,
            startDate: new moment().format('YYYY-MM-DD'),
			endDate: new moment().format('YYYY-MM-DD'),
            minEarly: 3,
            minLate: 3,
            loading: false,
            generated: false,
            printing : false,
            detailsLoading : false
		};
	},
	computed: {
		fields() {
            return [
				{ key: 'line', label: this.$t('stop_punctuality_report.line'), sortable: true },
				{ key: 'departureTime', label: this.$t('stop_punctuality_report.departure_time'), sortable: true,
                    formatter: function(value) {
                        var time = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('DD.MM.YYYY HH:mm');
                        if (time.startsWith('Invalid')) {
                            return '';
                        }
                        return time;
                    }
                },
				{ key: 'realTime', label: this.$t('stop_punctuality_report.real_time'), sortable: true,
                    formatter: function(value) {
                        var time = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('HH:mm:ss');
                        if (time.startsWith('Invalid')) {
                            return '';
                        }
                        return time;
                    }
                },
				{ key: 'deviation', label: this.$t('stop_punctuality_report.deviation'), sortable: true },
                { key: 'actions', label: '' }
            ];
        },
		items() {
            var itemList = this.$store.state.stopPunctualityReport.stopPunctualityReportList;                        
            return itemList.sort((a,b) => utils.alfaNumericCompare(a.line, b.line));
		},
        timeOptions() {
            var options = [];
            for (var i = 0; i < 11; i++) {
                options.push({ 'minutes': i, 'label': i + ' min.' });
            }
            return options;
        },
        buttonDisabled() {
			return !this.selectedStop || !this.startDate || !this.endDate 
					|| (moment(this.endDate, "YYYY-MM-DD") > moment(this.startDate, "YYYY-MM-DD").add(7, 'days') || this.startDate > this.endDate)
					|| this.loading || this.printing;
		},
		totalRows() {
			return this.items ? this.items.length : 0;
		},
	},
    beforeCreate() {
		if (!this.$store.state.stopPunctualityReport) {
            this.$store.registerModule("stopPunctualityReport", stopPunctualityReport);
        }
    },
    created(){
        window.addEventListener("afterprint", this.afterprint);
    },
    beforeDestroy(){
        window.removeEventListener("afterprint", this.afterprint);
    },
	methods: {
        getRowData(row) {
            if (!row.detailsShowing && (!row.item.passings || row.item.passings.length <= 1)) {
                /*row.toggleDetails();*/
                this.detailsLoading = true;

                var startTime = new moment(this.startDate, 'YYYY-MM-DD');
                var endTime = new moment(this.endDate, 'YYYY-MM-DD');
                var params = {
                    'line': row.item.line,
                    'stop': this.selectedStop,
                    'startTime': startTime.format('YYYY-MM-DD[T]HH:mm:ss'),
                    'endTime': endTime.format('YYYY-MM-DD[T]23:[59:59]'),
                    'minEarly': this.minEarly,
                    'minLate': this.minLate
                }
                this.$store.dispatch('stopPunctualityReport/getStopPassings', params)
                    .then(response => {
                        var rowItemsList = row.item.passings;
                        var stopPassingList = response.data[0].passings;
                        stopPassingList.sort( (a,b) => (Number(a.departureTime) > Number(b.departureTime)) ? 1 : ((Number(b.departureTime) > Number(a.departureTime)) ? -1 : 0) );

                        for (let i = 1; i < stopPassingList.length; i++) {
                            rowItemsList.push(stopPassingList[i]);
                        }
                    })
                    .finally(() => { row.toggleDetails(); this.detailsLoading = false; });
            } else
                row.toggleDetails();
        },
		onPageChange(page) {
			this.currentPage = page;
		},
        generateReport() {
            this.loading = true;

            var startTime = new moment(this.startDate, 'YYYY-MM-DD');
            var endTime = new moment(this.endDate, 'YYYY-MM-DD');
            var params = {
                'stop': this.selectedStop,
                'startTime': startTime.format('YYYY-MM-DD[T]HH:mm:ss'),
                'endTime': endTime.format('YYYY-MM-DD[T]23:[59:59]'),
                'minEarly': this.minEarly,
                'minLate': this.minLate,
                'getPassings': false
            }
            this.$store.dispatch('stopPunctualityReport/getStopPunctualityReport', params)
                .finally(() => { this.loading = false; this.generated = true });
        },
        generateXLS() {
            this.loading = true;

            var startTime = new moment(this.startDate, 'YYYY-MM-DD');
            var endTime = new moment(this.endDate, 'YYYY-MM-DD');
            var params = {
                'stop': this.selectedStop,
                'startTime': startTime.format('YYYY-MM-DD[T]HH:mm:ss'),
                'endTime': endTime.format('YYYY-MM-DD[T]23:[59:59]'),
                'minEarly': this.minEarly,
                'minLate': this.minLate,
                'getPassings': true,
                'header1': this.$t('stop_punctuality_report.line'),
                'header2': this.$t('stop_punctuality_report.stop'),
                'header3': this.$t('stop_punctuality_report.scheduled_time'),
                'header4': this.$t('stop_punctuality_report.real_time'),
                'header5': this.$t('stop_punctuality_report.deviation')
            }
            this.$store.dispatch('stopPunctualityReport/exportXLS', params)
                .finally(() => { this.loading = false; });
        },
        getDeviation(scheduledTime, realTime) {
            if (!scheduledTime || !realTime) {
                return '';
            }
            var scheduled = new moment(scheduledTime, 'YYYY-MM-DD[T]HH:mm:ssZZ');
            var real = new moment(realTime, 'YYYY-MM-DD[T]HH:mm:ssZZ');
            var deviation = new moment.duration(real.diff(scheduled)).asMilliseconds();
            if (deviation >= 0) {
                return new moment.utc(deviation).format('[+]HH:mm:ss');
            }
            return new moment.utc(new moment.duration(scheduled.diff(real)).asMilliseconds()).format('[-]HH:mm:ss');
        },
        formatTime(time, format) {
            var formatted = new moment(time, 'YYYY-MM-DD[T]HH:mm:ssZZ').format(format);
            if (formatted.startsWith('Invalid')) {
                return '';
            }
            return formatted;
        },
        printReport(){
            this.printing = true;
            this.perPage = this.items.length
            
            // timeout needed because table takes some time to render
            setTimeout(() => { window.print(); }, 10);   
        },
        afterprint(){
            this.perPage = 30;
            this.printing = false;
        }
    }
};
</script>